import { Box, Paper, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import AppButton from "../../../components/Button";
import Replicate from "../../../icons/Replicate";
import Send from "../../../icons/Send";
import CommittedConditionGroups from "./CommittedConditionGroups";
import ConditionSelectTable from "./ConditionSelectTable";
import {
  ConditionStatusEntry,
  SubmittedGroup,
  pivotSubmittedGroupsByCondition,
} from "./statusState";
import type { StatusSection } from "./StatusNavigation";

const ColumnCard = styled(Paper)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  maxHeight: "calc(100vh - 200px)",
  overflow: "hidden",
}));

type ConditionsListProps = {
  activeSection: StatusSection;
  search: string;
  onSearchChange: (value: string) => void;
  entries: ConditionStatusEntry[];
  totalEntries: number;
  filteredSubmittedGroups: SubmittedGroup[];
  selectedIds: number[];
  selectableIds: number[];
  areAllSelected: boolean;
  onToggle: (conditionId: number) => void;
  onToggleAll: () => void;
  onCreateCells: () => void;
  onAddReplicates: () => void;
  isCtaDisabled: boolean;
  isReadOnly: boolean;
  isValidationsLoading: boolean;
  conditionHasErrors: (condition: FormspecCondition) => boolean;
  activeModifyConditionId: number | null;
  modificationStatus: string | null;
  isCommitLoading: boolean;
  onModifyCondition: (conditionId: number) => void;
  onCancelModifyCondition: (conditionId: number) => void;
  onResubmitCondition: (conditionId: number) => void;
  onDeleteSubmission: (groupId: string) => void;
};

const SECTION_LABELS: Record<StatusSection, string> = {
  wip: "WIP Conditions",
  staged: "Staged Conditions",
  committed: "Committed+ Conditions",
};

const ConditionsList = ({
  activeSection,
  search,
  onSearchChange,
  entries,
  totalEntries,
  filteredSubmittedGroups,
  selectedIds,
  selectableIds,
  areAllSelected,
  onToggle,
  onToggleAll,
  onCreateCells,
  onAddReplicates,
  isCtaDisabled,
  isReadOnly,
  isValidationsLoading,
  conditionHasErrors,
  activeModifyConditionId,
  modificationStatus,
  isCommitLoading,
  onModifyCondition,
  onCancelModifyCondition,
  onResubmitCondition,
  onDeleteSubmission,
}: ConditionsListProps) => {
  const isWip = activeSection === "wip";
  const isCommitted = activeSection === "committed";
  const emptyLabel =
    totalEntries === 0
      ? `No ${SECTION_LABELS[activeSection].toLowerCase()}.`
      : `No ${SECTION_LABELS[activeSection].toLowerCase()} match your search.`;
  // In the committed section, submitted work-order group cards render their
  // own condition rows below this table. Suppress the empty-state table (and
  // its "No committed conditions" label) when there are no ordinary committed
  // rows but there are submitted groups to show.
  const showConditionTable =
    !isCommitted || entries.length > 0 || filteredSubmittedGroups.length === 0;

  return (
    <ColumnCard elevation={0}>
      <Typography
        variant="h6"
        sx={{ backgroundColor: "#f8f8f8", padding: "12px" }}
      >
        {SECTION_LABELS[activeSection]}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search conditions"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        {isWip ? (
          <AppButton
            color="primary"
            onClick={onCreateCells}
            disabled={isCtaDisabled}
            startIcon={<Send />}
            sx={{ minWidth: 160, whiteSpace: "nowrap" }}
          >
            Create Cells
          </AppButton>
        ) : (
          <AppButton
            color="secondary"
            onClick={onAddReplicates}
            disabled={isCtaDisabled}
            startIcon={<Replicate />}
            sx={{ width: "fit-content", whiteSpace: "nowrap" }}
          >
            ADD REPLICATES
          </AppButton>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {showConditionTable && (
          <ConditionSelectTable
            entries={entries}
            selectedIds={selectedIds}
            selectableIds={selectableIds}
            areAllSelected={areAllSelected}
            onToggle={onToggle}
            onToggleAll={onToggleAll}
            isReadOnly={isReadOnly}
            hasError={isWip ? conditionHasErrors : undefined}
            isLoading={isWip && isValidationsLoading}
            emptyLabel={emptyLabel}
            selectAllAriaLabel={`Select all visible ${SECTION_LABELS[
              activeSection
            ].toLowerCase()}`}
            fixedLayout
          />
        )}

        {isCommitted && filteredSubmittedGroups.length > 0 && (
          <CommittedConditionGroups
            conditionGroups={pivotSubmittedGroupsByCondition(
              filteredSubmittedGroups
            )}
            selectedIds={selectedIds}
            areAllSelected={areAllSelected}
            onToggle={onToggle}
            onToggleAll={onToggleAll}
            isReadOnly={isReadOnly}
            activeModifyConditionId={activeModifyConditionId}
            modificationStatus={modificationStatus}
            isCommitLoading={isCommitLoading}
            onModifyCondition={onModifyCondition}
            onCancelModifyCondition={onCancelModifyCondition}
            onResubmitCondition={onResubmitCondition}
            onDeleteSubmission={onDeleteSubmission}
          />
        )}
      </Box>
    </ColumnCard>
  );
};

export default ConditionsList;
